import * as NavigationMenuPrimitive from '@radix-ui/react-navigation-menu';
import { cva } from 'class-variance-authority';
import { ChevronDown } from 'lucide-react';
import * as React from 'react';

import { cn } from '@pray/shared/utils/styles';

/**
 * @type {React.ForwardRefExoticComponent<React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Root> & {className?: string | undefined; children?: React.ReactNode | undefined;} & React.RefAttributes<HTMLDivElement>>}
 */
const NavigationMenu = React.forwardRef(({ className, children, ...props }, ref) => (
  <NavigationMenuPrimitive.Root
    ref={ref}
    className={cn('relative z-10 flex max-w-max flex-1 items-center justify-center', className)}
    {...props}
  >
    {children}
    <NavigationMenuViewport />
  </NavigationMenuPrimitive.Root>
));
NavigationMenu.displayName = NavigationMenuPrimitive.Root.displayName;

/**
 * @type {React.ForwardRefExoticComponent<React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.List> & {className?: string | undefined; children?: React.ReactNode | undefined;} & React.RefAttributes<HTMLUListElement>>}
 */
const NavigationMenuList = React.forwardRef(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.List
    ref={ref}
    className={cn('group flex flex-1 list-none items-center justify-center space-x-1', className)}
    {...props}
  />
));
NavigationMenuList.displayName = NavigationMenuPrimitive.List.displayName;

const NavigationMenuItem = NavigationMenuPrimitive.Item;

const navigationMenuTriggerStyle = cva(
  'group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=open]:bg-accent/50 data-[state=open]:text-accent-foreground data-[state=open]:hover:bg-accent data-[state=open]:focus:bg-accent'
);

/**
 * @type {React.ForwardRefExoticComponent<React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Trigger> & { triggerMode?: "click" | "hover"; className?: string | undefined; children?: React.ReactNode | undefined;} & React.RefAttributes<HTMLButtonElement>>}
 */
const NavigationMenuTrigger = React.forwardRef(({ triggerMode = 'hover', className, children, ...props }, ref) => (
  <NavigationMenuPrimitive.Trigger
    ref={ref}
    className={cn(navigationMenuTriggerStyle(), 'group', className)}
    {...props}
    {...(triggerMode === 'click' && {
      onPointerEnter: (event) => event.preventDefault(),
      onPointerMove: (event) => event.preventDefault(),
      onPointerLeave: (event) => event.preventDefault(),
    })}
  >
    {children}{' '}
    <ChevronDown
      className="relative top-px ml-1 size-4 transition duration-200 group-data-[state=open]:rotate-180"
      aria-hidden="true"
    />
  </NavigationMenuPrimitive.Trigger>
));
NavigationMenuTrigger.displayName = NavigationMenuPrimitive.Trigger.displayName;

/**
 * @type {React.ForwardRefExoticComponent<React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Content> & { triggerMode?: "click" | "hover"; className?: string | undefined; children?: React.ReactNode | undefined;} & React.RefAttributes<HTMLDivElement>>}
 */
const NavigationMenuContent = React.forwardRef(({ triggerMode = 'hover', className, ...props }, ref) => (
  <NavigationMenuPrimitive.Content
    ref={ref}
    className={cn(
      'left-0 top-0 w-full data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52 md:absolute md:w-auto ',
      className
    )}
    {...props}
    {...(triggerMode === 'click' && {
      onPointerEnter: (event) => event.preventDefault(),
      onPointerMove: (event) => event.preventDefault(),
      onPointerLeave: (event) => event.preventDefault(),
    })}
  />
));
NavigationMenuContent.displayName = NavigationMenuPrimitive.Content.displayName;

const NavigationMenuLink = NavigationMenuPrimitive.Link;

/**
 * @type {React.ForwardRefExoticComponent<React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Viewport> & {className?: string | undefined; children?: React.ReactNode | undefined;} & React.RefAttributes<HTMLDivElement>>}
 */
const NavigationMenuViewport = React.forwardRef(({ className, ...props }, ref) => (
  <div className="absolute top-full flex justify-center">
    <NavigationMenuPrimitive.Viewport
      className={cn(
        'origin-top-center relative mt-1.5 h-[var(--radix-navigation-menu-viewport-height)] w-full overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 md:w-[var(--radix-navigation-menu-viewport-width)]',
        className
      )}
      ref={ref}
      {...props}
    />
  </div>
));
NavigationMenuViewport.displayName = NavigationMenuPrimitive.Viewport.displayName;

/**
 * @type {React.ForwardRefExoticComponent<React.ComponentPropsWithoutRef<typeof NavigationMenuPrimitive.Indicator> & {className?: string | undefined; children?: React.ReactNode | undefined;} & React.RefAttributes<HTMLDivElement>>}
 */
const NavigationMenuIndicator = React.forwardRef(({ className, ...props }, ref) => (
  <NavigationMenuPrimitive.Indicator
    ref={ref}
    className={cn(
      'top-full z-[1] flex h-1.5 items-end justify-center overflow-hidden data-[state=visible]:animate-in data-[state=hidden]:animate-out data-[state=hidden]:fade-out data-[state=visible]:fade-in',
      className
    )}
    {...props}
  >
    <div className="relative top-[60%] size-2 rotate-45 rounded-tl-sm bg-border shadow-md" />
  </NavigationMenuPrimitive.Indicator>
));
NavigationMenuIndicator.displayName = NavigationMenuPrimitive.Indicator.displayName;

export {
  navigationMenuTriggerStyle,
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuContent,
  NavigationMenuTrigger,
  NavigationMenuLink,
  NavigationMenuIndicator,
  NavigationMenuViewport,
};
